/* eslint-disable arrow-body-style */
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import Label from 'src/components/Label';
import { Item, QuestionTypesEnum } from 'src/@types/Course';

// ----------------------------------------------------------------------
// Modernised quiz-authoring UI. Same react-hook-form field paths and data
// shape as before (details.questions[i].{title,title_ar,description,
// description_ar,multi,type, answers[j].{name,name_ar,correct,points}}) — just a
// cleaner card-based layout with a green "correct" toggle, per-answer delete,
// an inline "needs a correct answer" hint, and question-type badges.

type Meta = { label: string; color: 'primary' | 'info' | 'warning' };
function typeMeta(type: QuestionTypesEnum): Meta {
  if (type === QuestionTypesEnum.MCQ) return { label: 'Multiple choice', color: 'primary' };
  if (type === QuestionTypesEnum.COMPLETE) return { label: 'Fill in the blank', color: 'info' };
  return { label: 'True / False', color: 'warning' };
}

// ---- shared card shell for a question ------------------------------------
const QuestionCard = ({
  number,
  type,
  onDelete,
  hasCorrect,
  children,
}: {
  number: number;
  type: QuestionTypesEnum;
  onDelete(): void;
  hasCorrect: boolean;
  children: React.ReactNode;
}) => {
  const meta = typeMeta(type);
  return (
    <Card variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1">Question {number}</Typography>
          <Label color={meta.color}>{meta.label}</Label>
        </Stack>
        <Tooltip title="Delete question">
          <IconButton color="error" size="small" onClick={onDelete}>
            <Iconify icon="eva:trash-2-outline" />
          </IconButton>
        </Tooltip>
      </Stack>
      {children}
      {!hasCorrect && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Mark at least one answer as correct — the quiz can&apos;t be saved otherwise.
        </Alert>
      )}
    </Card>
  );
};

// ---- the question text fields --------------------------------------------
const QuestionFields = ({
  type,
  index,
  setQuestionType,
}: {
  type: QuestionTypesEnum;
  index: number;
  setQuestionType: (index: number, type: QuestionTypesEnum) => void;
}) => (
  <Stack spacing={2}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <RHFTextField
        fullWidth
        label="Question (English)"
        onFocus={() => setQuestionType(index, type)}
        name={`details.questions[${index}].title`}
      />
      <RHFTextField
        fullWidth
        label="Question (Arabic)"
        onFocus={() => setQuestionType(index, type)}
        name={`details.questions[${index}].title_ar`}
      />
    </Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <RHFTextField
        fullWidth
        label="Description (optional)"
        multiline
        rows={2}
        name={`details.questions[${index}].description`}
      />
      <RHFTextField
        fullWidth
        label="Description AR (optional)"
        multiline
        rows={2}
        name={`details.questions[${index}].description_ar`}
      />
    </Stack>
    {type === QuestionTypesEnum.MCQ && (
      <RHFCheckbox
        label="Allow multiple correct answers"
        name={`details.questions[${index}].multi`}
      />
    )}
  </Stack>
);

// ---- a single answer row --------------------------------------------------
export const Answer = ({
  qindex,
  answerIndex,
  watch,
  setValue,
  onRemove,
  canRemove,
}: {
  qindex: number;
  answerIndex: number;
  watch: UseFormWatch<Item>;
  setValue: UseFormSetValue<Item>;
  onRemove?: () => void;
  canRemove?: boolean;
}) => {
  const correctPath = `details.questions[${qindex}].answers[${answerIndex}].correct`;
  const watchCorrect = watch(correctPath as any);

  // Keep points in sync with the correct flag (1 if correct, else 0).
  useEffect(() => {
    setValue(
      `details.questions[${qindex}].answers[${answerIndex}].points` as any,
      watchCorrect ? 1 : 0,
    );
  }, [watchCorrect, qindex, answerIndex, setValue]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        transition: 'border-color .15s, background-color .15s',
        border: (t) => `1px solid ${watchCorrect ? t.palette.success.main : t.palette.divider}`,
        bgcolor: (t) => (watchCorrect ? alpha(t.palette.success.main, 0.08) : 'transparent'),
      }}
    >
      <Stack spacing={1} flexGrow={1}>
        <RHFTextField
          size="small"
          label={`Answer ${answerIndex + 1} (English)`}
          name={`details.questions[${qindex}].answers[${answerIndex}].name`}
        />
        <RHFTextField
          size="small"
          label="Answer (Arabic)"
          name={`details.questions[${qindex}].answers[${answerIndex}].name_ar`}
        />
      </Stack>
      <Tooltip title={watchCorrect ? 'Correct answer' : 'Mark as correct'}>
        <Stack alignItems="center" sx={{ minWidth: 64 }}>
          <Switch
            color="success"
            checked={!!watchCorrect}
            onChange={(e) =>
              setValue(correctPath as any, e.target.checked, { shouldValidate: true })
            }
          />
          <Typography
            variant="caption"
            sx={{ color: watchCorrect ? 'success.main' : 'text.secondary' }}
          >
            Correct
          </Typography>
        </Stack>
      </Tooltip>
      {canRemove && onRemove && (
        <Tooltip title="Remove answer">
          <IconButton size="small" onClick={onRemove}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

// ---- True / False question -----------------------------------------------
export const TrueFalse = ({
  onDelete,
  index,
  setQuestionType,
  watch,
  setValue,
}: {
  onDelete(): void;
  index: number;
  setQuestionType: (index: number, type: QuestionTypesEnum) => void;
  watch: UseFormWatch<Item>;
  setValue: UseFormSetValue<Item>;
}) => {
  const qAnswers = (watch(`details.questions[${index}].answers` as any) as any[]) || [];
  const hasCorrect = Array.isArray(qAnswers) && qAnswers.some((a) => a?.correct);
  return (
    <QuestionCard
      number={index + 1}
      type={QuestionTypesEnum.trueFalse}
      onDelete={onDelete}
      hasCorrect={hasCorrect}
    >
      <QuestionFields
        setQuestionType={setQuestionType}
        index={index}
        type={QuestionTypesEnum.trueFalse}
      />
      <Stack spacing={1.5} mt={2}>
        <Typography variant="subtitle2">Options — mark the correct one</Typography>
        <Answer setValue={setValue} watch={watch} qindex={index} answerIndex={0} />
        <Answer setValue={setValue} watch={watch} qindex={index} answerIndex={1} />
      </Stack>
    </QuestionCard>
  );
};

// ---- MCQ + Complete questions --------------------------------------------
export const MCQ = ({
  onDelete,
  index,
  title,
  setQuestionType,
  editAnswers,
  watch,
  setValue,
}: {
  onDelete(): void;
  index: number;
  title: string;
  editAnswers: any[] | undefined;
  watch: UseFormWatch<Item>;
  setValue: UseFormSetValue<Item>;
  setQuestionType: (index: number, type: QuestionTypesEnum) => void;
}) => {
  const isMcq = title === 'MCQ';
  const type = isMcq ? QuestionTypesEnum.MCQ : QuestionTypesEnum.COMPLETE;
  const [count, setCount] = useState(editAnswers && editAnswers.length ? editAnswers.length : 1);

  const qAnswers = (watch(`details.questions[${index}].answers` as any) as any[]) || [];
  const hasCorrect = Array.isArray(qAnswers) && qAnswers.some((a) => a?.correct);

  const addAnswer = () => setCount((c) => c + 1);
  const removeAnswer = (j: number) => {
    const cur = ((watch(`details.questions[${index}].answers` as any) as any[]) || []).slice();
    cur.splice(j, 1);
    setValue(`details.questions[${index}].answers` as any, cur, { shouldValidate: true });
    setCount((c) => Math.max(1, c - 1));
  };

  return (
    <QuestionCard
      number={index + 1}
      type={type}
      onDelete={onDelete}
      hasCorrect={isMcq ? hasCorrect : qAnswers.length > 0}
    >
      <QuestionFields setQuestionType={setQuestionType} index={index} type={type} />
      <Stack spacing={1.5} mt={2}>
        <Typography variant="subtitle2">{isMcq ? 'Answers' : 'Expected answer'}</Typography>
        {Array.from({ length: count }).map((_, answerIndex) => (
          <Answer
            key={answerIndex}
            setValue={setValue}
            watch={watch}
            answerIndex={answerIndex}
            qindex={index}
            canRemove={isMcq && count > 1}
            onRemove={() => removeAnswer(answerIndex)}
          />
        ))}
        {isMcq && (
          <Box>
            <Button size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={addAnswer}>
              Add answer
            </Button>
          </Box>
        )}
      </Stack>
    </QuestionCard>
  );
};
